import { FC } from "react";

/**
 * Анимированная SVG-иллюстрация для шапки «Политика конфиденциальности»:
 * щит с замком, вращающееся сканирующее кольцо, пульсирующие защитные
 * дуги и парящие элементы. Только transform/opacity + dashoffset —
 * дёшево для GPU; при prefers-reduced-motion всё замирает.
 */
export const PrivacyIllustration: FC<{ className?: string }> = ({ className }) => (
  <div className={className} aria-hidden>
    <style>{`
      .pv-g{transform-box:fill-box;transform-origin:center}
      @keyframes pv-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
      @keyframes pv-float2{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
      @keyframes pv-scan{to{transform:rotate(360deg)}}
      @keyframes pv-scan-ccw{to{transform:rotate(-360deg)}}
      @keyframes pv-pulse{0%,100%{transform:scale(1);opacity:.4}50%{transform:scale(1.05);opacity:.85}}
      @keyframes pv-twinkle{0%,100%{opacity:.12;transform:scale(.7)}50%{opacity:.65;transform:scale(1)}}
      .pv-shield{animation:pv-float 6s ease-in-out infinite}
      .pv-scan{animation:pv-scan 18s linear infinite}
      .pv-ring{animation:pv-scan-ccw 46s linear infinite}
      .pv-halo{animation:pv-pulse 3.6s ease-in-out infinite}
      .pv-dot{animation:pv-float2 5s ease-in-out .6s infinite}
      .pv-t1{animation:pv-twinkle 3s ease-in-out infinite}
      .pv-t2{animation:pv-twinkle 3.7s ease-in-out 1s infinite}
      .pv-t3{animation:pv-twinkle 3.3s ease-in-out 2s infinite}
      @media (prefers-reduced-motion:reduce){
        .pv-shield,.pv-scan,.pv-ring,.pv-halo,.pv-dot,.pv-t1,.pv-t2,.pv-t3{animation:none}
      }
    `}</style>

    <svg viewBox="0 0 240 240" fill="none" className="h-full w-full">
      <defs>
        <radialGradient id="pv-glow" cx="50%" cy="46%" r="52%">
          <stop offset="0%" stopColor="rgba(208,237,28,0.14)" />
          <stop offset="60%" stopColor="rgba(208,237,28,0.04)" />
          <stop offset="100%" stopColor="rgba(208,237,28,0)" />
        </radialGradient>
        <linearGradient id="pv-shield-f" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor="#f9f6ee" />
          <stop offset="100%" stopColor="#e2ddd0" />
        </linearGradient>
      </defs>

      {/* свечение */}
      <circle cx="120" cy="118" r="108" fill="url(#pv-glow)" />

      {/* пульсирующие защитные дуги */}
      <g className="pv-g pv-halo">
        <circle cx="120" cy="118" r="88" stroke="rgba(242,239,232,0.08)" strokeWidth="1.4" fill="none" />
      </g>

      {/* внешнее вращающееся кольцо */}
      <g className="pv-g pv-ring">
        <circle
          cx="120" cy="118" r="98"
          stroke="rgba(242,239,232,0.16)" strokeWidth="1.4"
          strokeDasharray="2 12" strokeLinecap="round"
        />
      </g>

      {/* сканирующее кольцо с «бегунком» и частицами */}
      <g className="pv-g pv-scan">
        <circle
          cx="120" cy="118" r="78"
          stroke="rgba(208,237,28,0.22)" strokeWidth="1.6"
          strokeDasharray="46 300" strokeLinecap="round" fill="none"
        />
        <circle cx="120" cy="40" r="4" fill="#d0ed1c" />
        <circle cx="52" cy="79" r="2.6" fill="rgba(208,237,28,0.6)" />
        <circle cx="180" cy="168" r="2.6" fill="rgba(208,237,28,0.6)" />
      </g>

      {/* щит */}
      <g className="pv-g pv-shield">
        {/* тень */}
        <path
          d="M120 52l50 18v42c0 32-22 55-50 66-28-11-50-34-50-66V70Z"
          fill="rgba(0,0,0,0.28)" transform="translate(0 5)"
        />
        {/* корпус */}
        <path
          d="M120 48l50 18v42c0 32-22 55-50 66-28-11-50-34-50-66V66Z"
          fill="url(#pv-shield-f)"
        />
        {/* боковая подсветка (объём формы) — левая половина светлее */}
        <path
          d="M120 48L70 66v42c0 32 22 55 50 66Z"
          fill="rgba(255,255,255,0.18)"
        />
        {/* лаймовый кант */}
        <path
          d="M120 48l50 18v42c0 32-22 55-50 66-28-11-50-34-50-66V66Z"
          stroke="rgba(208,237,28,0.5)" strokeWidth="2" fill="none"
        />
        {/* внутренняя обводка */}
        <path
          d="M120 62l38 14v34c0 25-17 43-38 52-21-9-38-27-38-52V76Z"
          stroke="rgba(48,42,65,0.1)" strokeWidth="1.4" fill="none"
        />

        {/* замок */}
        <g transform="translate(120 116)">
          {/* дужка */}
          <path d="M-13 -6v-8a13 13 0 0 1 26 0v8" stroke="#302a41" strokeWidth="5" strokeLinecap="round" fill="none" />
          {/* корпус */}
          <rect x="-19" y="-6" width="38" height="30" rx="7" fill="#302a41" />
          {/* скважина */}
          <circle cx="0" cy="4" r="4.5" fill="#d0ed1c" />
          <path d="M0 6l-3 10h6Z" fill="#d0ed1c" />
        </g>

      </g>

      {/* парящая точка-щиток */}
      <g className="pv-g pv-dot">
        <g transform="translate(50 82)">
          <circle r="14" fill="#d0ed1c" />
          <path d="M0 -7l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10v-5Z" fill="#302a41" />
        </g>
      </g>

      {/* искры */}
      <g stroke="#d0ed1c" strokeWidth="2.4" strokeLinecap="round">
        <g className="pv-g pv-t1"><path d="M42 152v11M36.5 157.5h11" /></g>
        <g className="pv-g pv-t2"><path d="M196 96v10M191 101h10" /></g>
        <g className="pv-g pv-t3"><path d="M184 178v9M179.5 182.5h9" /></g>
      </g>
    </svg>
  </div>
);
